import {get, isEmpty} from "lodash";
import {createSelector} from "reselect";
import {
  COUNTRY_CODES,
  CUSTOMER_SEGMENT_ENUM,
} from "../../../settings/settings";

export const getPersonalInformationData = (state) =>
  state.personalInformation.data;

export const getUserMessagesCount = (state) =>
  state.personalInformation.messagesNumber;

export const getPersonalInformationLoading = (state) =>
  state.personalInformation.isLoading;

export const getPersonalInformationFailed = (state) =>
  state.personalInformation.hasFailed;

export const getDateOfBirth = createSelector(
  getPersonalInformationData,
  (personalInformation) => personalInformation.dateOfBirth
);

export const getPrimaryAddress = createSelector(
  getPersonalInformationData,
  /**
   * @param {Object} informationData
   * @returns {Object}
   */
  (informationData) => {
    const { fullName, primaryAddress } = informationData || {};
    return { fullName, ...primaryAddress };
  }
);

export const getIsEligibleRMD = createSelector(
  getPersonalInformationData,
  (personalInformation) =>
    personalInformation.eligibleForRMD
);

export const getUserAddress = createSelector(
  getPersonalInformationData,
  (personalInformation) =>
    personalInformation.primaryAddress ? personalInformation.primaryAddress : {}
);

export const getIsAddressAvailable = createSelector(
  getUserAddress,
  (userAddress) => {
    const street1 = get(userAddress, "street1");
    const city = get(userAddress, "city");
    const state = get(userAddress, "state");
    const zip = get(userAddress, "zip");
    const countryCode = get(userAddress, "countryCode");
    const isStateOptional =
      countryCode !== "US" && COUNTRY_CODES.includes(countryCode);
    return (
      !isEmpty(street1) &&
      !isEmpty(city) &&
      (isStateOptional || !isEmpty(state)) &&
      !isEmpty(zip) &&
      !isEmpty(countryCode)
    );
  }
);

export const getIsForeginAddress = createSelector(
  getUserAddress,
  (userAddress) => {
    const countryCode = get(userAddress, "countryCode");
    return COUNTRY_CODES.includes(countryCode);
  }
);

export const getUserEntitySegment = (state) => {
  if (
    state.personalInformation.data &&
    state.personalInformation.data.entitySegment
  ) {
    return state.personalInformation.data.entitySegment;
  }
};

export const getClientType = (state) => {
  if (state.personalInformation.data) {
    return state.personalInformation.data.eligibleForRMD;
  }
};

export const getCustomerSegmentName = createSelector(
  getUserEntitySegment,
  (entitySegment) => {
    if (entitySegment && entitySegment.enumerationID) {
      const id = entitySegment.enumerationID;
      if (CUSTOMER_SEGMENT_ENUM[id]) {
        return CUSTOMER_SEGMENT_ENUM[id];
      }
    }
  }
);

export const getCustomerEntitySegmentId = createSelector(
  getUserEntitySegment,
  (entitySegment) => {
    if (entitySegment && entitySegment.enumerationID) {
      return entitySegment.enumerationID;
    }
  }
);

export const isClientRMD = createSelector(getClientType, (isRMD) => isRMD);

// Check householdsegment information to show/hide custom distribution method option
export const getHouseholdSegmentInformation = createSelector(
  getUserEntitySegment,
  (entitySegment) => {
    let enableDistributionOption = false;
    const householdEntityName =
      entitySegment && entitySegment.enumerationName
        ? entitySegment.enumerationName
        : "";
    if (
      householdEntityName === "Enhanced Personal Services" || householdEntityName === "Personal Services"
    ) {
      enableDistributionOption = true;
    }
    return enableDistributionOption;
  }
);

// Check for personal service or enhanced personal service
export const getHouseholdEntityName = createSelector(
  getUserEntitySegment,
  (entitySegment) => {
    const householdEntityName =
      entitySegment && entitySegment.enumerationName
        ? entitySegment.enumerationName
        : "";
    return householdEntityName;
  }
);

export const getUserBPID = createSelector(
  [getPersonalInformationData],
  (personalInfoData) => {
    return personalInfoData?.businessPartyId;
  }
);

// To Be Used to pull household info in entity/details call instead of nonexistent household call
export const getHouseholdBalanceTierStatus = createSelector(
  [getPersonalInformationData],
  (personalInformation) => {
    return personalInformation && personalInformation.entitySegment.enumerationName;
  }
);
